import React, { useMemo, useState } from 'react'
import { inertiaSave, usePricingReview } from '@/hooks/usePricingReview'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { HelpTooltip } from '@/components/HelpTooltip'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Trash2, Pencil, Calendar, DollarSign, Shield } from 'lucide-react'
import { cn } from '@/lib/utils'
import { currencySymbol } from '@/lib/calendar-utils'
import { formatMoneySym } from '@/lib/format'
import { bandOverrides, type OverrideBand } from '@/lib/override-bands'
import { spansHotels } from '@/lib/reporting-scope'
import HotelChip from '@/components/groups/HotelChip'

export interface Override {
  id: number
  date: string
  /** Which hotel this override belongs to (Groups) — always sent; see BandableOverride. */
  propertyId?: number | null
  propertyName?: string | null
  roomType: string
  ratePlan: string
  overrideType: string
  /** 'relative' rows carry a percentage instead of an amount. */
  mode?: string
  adjustValue?: number | null
  /** Wall-clock date on which this override stops applying. */
  releaseOn?: string | null
  rateAmount: number | null
  stopSell: boolean | null
  closedToArrival: boolean | null
  closedToDeparture: boolean | null
  minStayArrival: number | null
  minStayThrough: number | null
  maxStay: number | null
  reason: string
  createdBy: string | null
  updatedBy: string | null
  createdAt: string
}

interface OverrideTableProps {
  overrides: Override[]
  /** The property's backend-served canonical path. Every request target is this + a suffix. */
  propertyBasePath: string
  currency: string
  /** Groups: the unit's lead, so its rows wear the crown rather than a plain hotel chip. */
  leadPropertyId?: number | null
}

const typeConfig: Record<string, { icon: React.ElementType; label: string; color: string; bg: string }> = {
  rate: { icon: DollarSign, label: 'Rate', color: 'text-emerald-700', bg: 'bg-emerald-50' },
  restriction: { icon: Shield, label: 'Restriction', color: 'text-amber-700', bg: 'bg-amber-50' },
  both: { icon: Calendar, label: 'Both', color: 'text-blue-700', bg: 'bg-blue-50' },
}

function restrictionsSummary(o: Override): string {
  const parts: string[] = []
  if (o.stopSell) parts.push('Stop Sell')
  if (o.closedToArrival) parts.push('CTA')
  if (o.closedToDeparture) parts.push('CTD')
  if (o.minStayArrival) parts.push(`Min Stay ${o.minStayArrival}`)
  if (o.minStayThrough) parts.push(`Min Through ${o.minStayThrough}`)
  if (o.maxStay) parts.push(`Max Stay ${o.maxStay}`)
  return parts.join(', ') || '-'
}

function formatDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

function shortDate(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

/** The date cell for a band: a single day, or a run with its length. */
function BandDates({ band }: { band: OverrideBand<Override> }) {
  if (band.days === 1) return <>{formatDate(band.startDate)}</>
  return (
    <span className="flex flex-col">
      <span>
        {shortDate(band.startDate)} – {formatDate(band.endDate)}
      </span>
      <span className="text-[10px] font-normal tabular-nums text-muted-foreground">{band.days} days</span>
    </span>
  )
}

export default function OverrideTable({ overrides, propertyBasePath, currency, leadPropertyId }: OverrideTableProps) {
  const sym = currencySymbol(currency)
  // Only once the list actually spans hotels — invisible for an ungrouped property.
  const showHotels = spansHotels(overrides)
  // Contiguous identical rows read as one line. Derived every render from the
  // rows themselves — there is no stored grouping to fall out of sync.
  const bands = useMemo(() => bandOverrides(overrides), [overrides])
  const [editingOverride, setEditingOverride] = useState<Override | null>(null)
  const [editRate, setEditRate] = useState('')
  const [editStopSell, setEditStopSell] = useState(false)
  const [editCTA, setEditCTA] = useState(false)
  const [editCTD, setEditCTD] = useState(false)
  const [editMinStay, setEditMinStay] = useState('')
  const [editMaxStay, setEditMaxStay] = useState('')
  const [editReason, setEditReason] = useState('')
  const [saving, setSaving] = useState(false)
  // The edit dialog steps aside while its review is open, and comes back, fields intact, on "Back".
  const [editHidden, setEditHidden] = useState(false)
  const review = usePricingReview()
  const reviewEndpoint = `${propertyBasePath}/overrides/review`

  function openEdit(o: Override) {
    setEditingOverride(o)
    setEditRate(o.rateAmount != null ? String(o.rateAmount) : '')
    setEditStopSell(o.stopSell ?? false)
    setEditCTA(o.closedToArrival ?? false)
    setEditCTD(o.closedToDeparture ?? false)
    setEditMinStay(o.minStayArrival != null ? String(o.minStayArrival) : '')
    setEditMaxStay(o.maxStay != null ? String(o.maxStay) : '')
    setEditReason(o.reason)
  }

  function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!editingOverride) return
    const id = editingOverride.id

    const data = {
      override: {
        rate_amount: editRate || '',
        stop_sell: editStopSell,
        closed_to_arrival: editCTA,
        closed_to_departure: editCTD,
        min_stay_arrival: editMinStay || '',
        max_stay: editMaxStay || '',
        reason: editReason,
      },
    }
    setEditHidden(true)
    review.open(
      reviewEndpoint,
      { operation: 'update', id, ...data },
      () =>
        inertiaSave('patch', `${propertyBasePath}/overrides/${id}`, data, {
          onStart: () => setSaving(true),
          onFinish: () => {
            setSaving(false)
            setEditingOverride(null)
            setEditHidden(false)
          },
        }),
      { onBack: () => setEditHidden(false) }
    )
  }

  // Deleting a run removes every day in it: the band is a view over those rows, so `also` names the
  // rest, and the server review states the real count and the dates (it replaces the confirm()).
  function handleDeleteBand(band: OverrideBand<Override>) {
    const data = band.days > 1 ? { also: band.members.slice(1).map((m) => m.id) } : {}
    review.open(reviewEndpoint, { operation: 'destroy', id: band.head.id, ...data }, () =>
      inertiaSave('delete', `${propertyBasePath}/overrides/${band.head.id}`, data)
    )
  }

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-border/60 bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border/40">
              <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Date</th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Room Type</th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Type</th>
              <th className="px-3 py-3 text-right text-xs font-semibold uppercase tracking-wider text-muted-foreground">Rate</th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Restrictions</th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">Reason</th>
              <th className="w-[80px] px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {bands.map((band, index) => {
              const o = band.head
              const config = typeConfig[o.overrideType] || typeConfig.rate
              const relative = o.mode === 'relative' && o.adjustValue != null
              return (
                <tr key={band.key} className={cn(
                  'border-b border-border/30 transition-colors hover:bg-muted/20',
                  index % 2 === 1 && 'bg-slate-50/30'
                )}>
                  <td className="px-5 py-3 text-[13px] font-semibold text-foreground whitespace-nowrap">
                    <BandDates band={band} />
                  </td>
                  <td className="px-3 py-3 text-[13px] text-foreground">
                    <span className="flex flex-col gap-0.5">
                      <span>{o.roomType}</span>
                      {showHotels && (
                        <HotelChip
                          name={o.propertyName}
                          propertyId={o.propertyId}
                          isLead={leadPropertyId != null && o.propertyId === leadPropertyId}
                          className="self-start"
                        />
                      )}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge className={cn('text-[10px] font-semibold', config.bg, config.color, 'hover:' + config.bg)}>
                        {config.label}
                      </Badge>
                      {relative && (
                        <Badge className="bg-blue-50 text-[10px] font-semibold text-blue-700 hover:bg-blue-50">
                          % each day
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right text-[13px] font-semibold tabular-nums">
                    {relative ? (
                      <span className="text-blue-700">
                        {o.adjustValue! > 0 ? '+' : ''}{o.adjustValue}%
                      </span>
                    ) : o.rateAmount != null ? (
                      formatMoneySym(o.rateAmount, sym)
                    ) : (
                      <span className="text-muted-foreground/40">-</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-xs text-muted-foreground">
                    {restrictionsSummary(o)}
                  </td>
                  <td className="px-3 py-3 max-w-[180px]">
                    <div className="truncate text-xs text-muted-foreground" title={o.reason}>{o.reason}</div>
                    {o.releaseOn && (
                      <div className="mt-0.5 truncate text-[10px] font-medium text-amber-700">
                        Releases {shortDate(o.releaseOn)}
                      </div>
                    )}
                    {(o.updatedBy || o.createdBy) && (
                      <div className="mt-0.5 truncate text-[10px] text-muted-foreground/60" title={o.updatedBy ? `edited by ${o.updatedBy}` : `by ${o.createdBy}`}>
                        {o.updatedBy ? `edited by ${o.updatedBy}` : `by ${o.createdBy}`}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => openEdit(o)}
                        aria-label="Edit override"
                        // Editing one row of a run would split the band — send the
                        // operator to the calendar, where the span is the unit of work.
                        disabled={band.days > 1}
                        title={band.days > 1 ? 'Edit multi-day runs from the calendar day view' : undefined}
                      >
                        <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() => handleDeleteBand(band)}
                        aria-label={band.days > 1 ? `Delete ${band.days} overrides` : 'Delete override'}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Edit Override Dialog */}
      <Dialog open={editingOverride != null && !editHidden} onOpenChange={(open) => { if (!open) setEditingOverride(null) }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Override</DialogTitle>
            <DialogDescription>
              {editingOverride && `${editingOverride.roomType}, ${formatDate(editingOverride.date)}`}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleEditSubmit} className="flex min-h-0 flex-1 flex-col">
            <DialogBody className="space-y-4">
              <div className="space-y-1">
                <div className="flex items-center gap-1">
                  <Label htmlFor="edit-rate" className="text-xs">Fixed Rate</Label>
                  <HelpTooltip content="Leave empty to remove the rate override and keep only restrictions." />
                </div>
                <Input
                  id="edit-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editRate}
                  onChange={(e) => setEditRate(e.target.value)}
                  placeholder="Leave empty for no rate override"
                />
              </div>

              <div className="space-y-2.5 rounded-xl border border-border/60 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Restrictions</p>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="edit-stop" className="text-xs cursor-pointer">Stop Sell</Label>
                    <p className="text-[10px] text-muted-foreground">Block from being sold</p>
                  </div>
                  <Switch id="edit-stop" checked={editStopSell} onCheckedChange={setEditStopSell} />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="edit-cta" className="text-xs cursor-pointer">Closed to Arrival</Label>
                    <p className="text-[10px] text-muted-foreground">Can't check in on this date</p>
                  </div>
                  <Switch id="edit-cta" checked={editCTA} onCheckedChange={setEditCTA} />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="edit-ctd" className="text-xs cursor-pointer">Closed to Departure</Label>
                    <p className="text-[10px] text-muted-foreground">Can't check out on this date</p>
                  </div>
                  <Switch id="edit-ctd" checked={editCTD} onCheckedChange={setEditCTD} />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="edit-min" className="text-[11px]">Min Stay</Label>
                    <Input id="edit-min" type="number" min="1" value={editMinStay} onChange={(e) => setEditMinStay(e.target.value)} placeholder="nights" />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="edit-max" className="text-[11px]">Max Stay</Label>
                    <Input id="edit-max" type="number" min="1" value={editMaxStay} onChange={(e) => setEditMaxStay(e.target.value)} placeholder="nights" />
                  </div>
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="edit-reason" className="text-xs">Reason</Label>
                <Input
                  id="edit-reason"
                  value={editReason}
                  onChange={(e) => setEditReason(e.target.value)}
                  required
                />
              </div>
            </DialogBody>

            <DialogFooter>
              <Button type="button" variant="outline" className="rounded-lg" onClick={() => setEditingOverride(null)}>Cancel</Button>
              <Button type="submit" className="rounded-lg" disabled={saving}>
                {saving ? 'Saving...' : 'Update Override'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      {review.dialog}
    </>
  )
}
